import { useCallback, useEffect, useState } from 'react';
import { produce } from 'immer';
import { RootState, useAppDispatch, useAppSelector } from '../../../logic/store/Store';
import { getDiscountReasonCodes, getPlanDiscountMatrix, getPlanDiscounts, updatePlanDiscounts } from '../../../logic/store/CompetitiveAllowance/CompetitiveAllowanceThunks';
import { cornId, soyId } from '../../../logic/store/Seeds/CropsSlice';
import { IDiscountReasonCode } from '../../../logic/Models/Responses/DiscountReasonCodeResponse';
import { ICompetitiveAllowanceResponse, IPlanCropDiscount, IPlanHybridDiscount, IPlanLocalGrowerDiscount } from '../../../logic/Models/Responses/CompetitiveAllowanceResponse';
import { cloneDeep } from 'lodash';
import { CropKey, cropIds, cropKeys } from './CompetitiveAllowanceMain';
import { RoundPrice } from '../../../logic/Utility/CalculationUtilities';
import { CropAmounts } from './pricingMatrix';

export interface IDiscountState {
	reasonCodes?: Record<CropKey, IDiscountReasonCode[]>;
	discounts?: Record<CropKey, {
		crop: IPlanCropDiscount[];
		hybrids: IPlanHybridDiscount[];
	}>;
	localGrower?: IPlanLocalGrowerDiscount | null;
	staleCodesCleared?: boolean;
}

// PUT body from the draft. LocalGrowerDiscount is only sent when the GET returned one;
// Percent null (reset to default) must be an explicit null, JSON.stringify drops undefined.
export function buildSavePayload(discountState: IDiscountState): ICompetitiveAllowanceResponse
{
	return {
		PlanCropDiscounts: [
			...discountState.discounts?.Corn.crop || [],
			...discountState.discounts?.Soybean.crop || []
		],
		PlanHybridDiscounts: [
			...discountState.discounts?.Corn.hybrids || [],
			...discountState.discounts?.Soybean.hybrids || []
		],
		...(discountState.localGrower ? { LocalGrowerDiscount: discountState.localGrower } : {}),
	};
}

// Live CA dollars per crop - the same sum the crop grid's total row shows.
export function competitiveAllowanceByCropFromDraft(discountState: IDiscountState): CropAmounts
{
	const result: CropAmounts = {};
	(['Corn', 'Soybean'] as CropKey[]).forEach(cropKey =>
	{
		const crop = discountState.discounts?.[cropKey];
		if (!crop)
		{
			return;
		}
		const cropLevelPerBag = crop.crop.reduce((acc, item) => acc + (item.PerBagDiscount || 0), 0);
		result[cropKey] = RoundPrice(crop.hybrids
			.flatMap(h => h.Products.map(p => (cropLevelPerBag + (h.PerBagDiscount || 0)) * p.Quantity))
			.reduce((acc, value) => acc + value, 0));
	});
	return result;
}

// Reason-code Ids are opaque tokens from Salesforce; the cutover changed their values, so a
// plan created before the cutover can hold a DiscountReasonCode Id that no longer matches any
// currently-offered option. Clear those in place so the row renders as unselected (error
// border / save-disabled) instead of silently holding a stale Id that still submits.
// `validCodes` is defensively treated as possibly missing: the backend guarantees every crop
// key, but if it doesn't, treat that crop as offering no options rather than throwing.
function clearStaleReasonCodes(rows: { DiscountReasonCode: string }[], validCodes: IDiscountReasonCode[])
{
	const offeredCodes = validCodes ?? [];
	rows.forEach(row =>
	{
		if (row.DiscountReasonCode && !offeredCodes.some(code => code.Id === row.DiscountReasonCode))
		{
			row.DiscountReasonCode = '';
		}
	});
}

export function useDiscountEditing()
{
	const dispatch = useAppDispatch();
	const {
		SelectedPlan,
	} = useAppSelector((state: RootState) => ({
		SelectedPlan: state.ui.SelectedPlanId ? state.grower.Growers.find(g => g.Id === state.ui.SelectedGrowerId)?.Plans.find(p => p.Id === state.ui.SelectedPlanId) : undefined,
	}));
	const GetReasonCodes = useCallback(() => dispatch(getDiscountReasonCodes()), [dispatch]);
	const GetDiscounts = useCallback(() => dispatch(getPlanDiscounts()), [dispatch]);
	const SaveDiscounts = useCallback((discounts) => dispatch(updatePlanDiscounts(discounts)), [dispatch]);
	const GetDiscountMatrix = useCallback(() => dispatch(getPlanDiscountMatrix()), [dispatch]);

	const [discountState, setDiscountState] = useState<IDiscountState>({});
	// Unsaved edits: the page shows a client-side estimate of the pricing breakdown until Save.
	const [isDirty, setIsDirty] = useState(false);

	// Load reason codes. Always fetches fresh from the server on mount (once - GetReasonCodes is
	// referentially stable) and reads the result off the dispatch promise rather than the
	// userInfo store slice: that slice is redux-persisted and rehydrates with a stale,
	// pre-cutover option list before this request resolves, which defeats stale-Id clearing
	// below. Leaves reasonCodes undefined on failure so the plan-cloning effect can still run
	// with empty dropdowns instead of the page getting stuck on a loading/empty state forever.
	useEffect(() =>
	{
		let cancelled = false;

		GetReasonCodes().then(resultAction =>
		{
			if (cancelled)
			{
				return;
			}

			if (getDiscountReasonCodes.fulfilled.match(resultAction))
			{
				const payload = resultAction.payload;
				setDiscountState(prevState => ({
					...prevState,
					reasonCodes: {
						Corn: payload[cornId],
						Soybean: payload[soyId],
					}
				}));
			}
		});

		return () =>
		{
			cancelled = true;
		};
	}, [GetReasonCodes]);

	// Load discounts and the pricing breakdown
	useEffect(() =>
	{
		if (SelectedPlan && discountState.discounts === undefined)
		{
			GetDiscounts();
			GetDiscountMatrix();
		}
	}, [SelectedPlan, discountState.discounts, GetDiscounts, GetDiscountMatrix]);

	// Make a local copy for editing as soon as the plan's Competitive Allowance rows are
	// available. Intentionally NOT gated on reasonCodes: if the reason-code fetch fails, rows
	// must still render (with empty dropdowns) rather than the page falsely reporting no
	// available hybrids.
	useEffect(() =>
	{
		if (SelectedPlan?.CompetitiveAllowance && !discountState.discounts)
		{
			setDiscountState(prevState => ({
				...prevState,
				discounts: {
					Corn: {
						crop: cloneDeep(SelectedPlan.CompetitiveAllowance.PlanCropDiscounts.filter(d => d.CropId === cornId)),
						hybrids: cloneDeep(SelectedPlan.CompetitiveAllowance.PlanHybridDiscounts.filter(d => d.CropId === cornId)),
					},
					Soybean: {
						crop: cloneDeep(SelectedPlan.CompetitiveAllowance.PlanCropDiscounts.filter(d => d.CropId === soyId)),
						hybrids: cloneDeep(SelectedPlan.CompetitiveAllowance.PlanHybridDiscounts.filter(d => d.CropId === soyId)),
					},
				},
				localGrower: SelectedPlan.CompetitiveAllowance.LocalGrowerDiscount ?? null,
			}));
		}
	}, [SelectedPlan?.CompetitiveAllowance, discountState.discounts]);

	// One-shot sanitize: once both the cloned rows and the fresh reason codes are available,
	// clear any stale (pre-cutover) DiscountReasonCode Id against the current option set.
	// Gated on staleCodesCleared so it runs exactly once per mount - after that, edits the user
	// makes (which can only choose from the already-offered options, since the dropdown has
	// nothing to select before reasonCodes arrives) are never re-clobbered.
	useEffect(() =>
	{
		if (discountState.discounts && discountState.reasonCodes && !discountState.staleCodesCleared)
		{
			setDiscountState(prevState => produce(prevState, draft =>
			{
				clearStaleReasonCodes(draft.discounts.Corn.crop, draft.reasonCodes.Corn);
				clearStaleReasonCodes(draft.discounts.Corn.hybrids, draft.reasonCodes.Corn);
				clearStaleReasonCodes(draft.discounts.Soybean.crop, draft.reasonCodes.Soybean);
				clearStaleReasonCodes(draft.discounts.Soybean.hybrids, draft.reasonCodes.Soybean);
				draft.staleCodesCleared = true;
			}));
		}
	}, [discountState.discounts, discountState.reasonCodes, discountState.staleCodesCleared]);

	const addCropLevelRow = useCallback((crop: CropKey) =>
	{
		setIsDirty(true);
		setDiscountState(prevState => ({
			...prevState,
			discounts: {
				...prevState.discounts,
				[crop]: {
					...prevState.discounts[crop],
					crop: [
						...prevState.discounts[crop].crop,
						{
							Id: null,
							PlanId: SelectedPlan.Id,
							CropId: cropIds[crop],
							PerBagDiscount: 0,
							DiscountReasonCode: '',
						}
					]
				}
			}
		}));
	}, [SelectedPlan?.Id]);

	const updateCropLevelRow = useCallback((cropId: string, index: number, updatedDiscount: number, originalDiscount: number, reasonCode?: string) =>
	{
		setIsDirty(true);
		setDiscountState(prevState =>
		{
			return produce(prevState, draft =>
			{
				draft.discounts[cropKeys[cropId]].crop[index].PerBagDiscount = RoundPrice(updatedDiscount);
				draft.discounts[cropKeys[cropId]].crop[index].DiscountReasonCode = reasonCode ? reasonCode : '';
			});
		});
	}, []);

	const deleteCropLevelRow = useCallback((cropId: string, index: number) =>
	{
		setIsDirty(true);
		setDiscountState(prevState =>
		{
			return produce(prevState, draft =>
			{
				draft.discounts[cropKeys[cropId]].crop.splice(index, 1);
			});
		});
	}, []);

	const updateHybridLevelRow = useCallback((hybridDiscountItem: IPlanHybridDiscount) =>
	{
		setIsDirty(true);
		setDiscountState(prevState =>
		{
			return produce(prevState, draft =>
			{
				const hybrid = draft.discounts[cropKeys[hybridDiscountItem.CropId]]
					.hybrids.find(h => h.HybridId === hybridDiscountItem.HybridId);

				if (hybrid)
				{
					hybrid.DiscountReasonCode = hybridDiscountItem.DiscountReasonCode;
					hybrid.PerBagDiscount = RoundPrice(hybridDiscountItem.PerBagDiscount);
				}
			});
		});
	}, []);

	// null = inherit the benefit's default percent.
	const updateLocalGrower = useCallback((percent: number | null) =>
	{
		setIsDirty(true);
		setDiscountState(prevState =>
		{
			return produce(prevState, draft =>
			{
				if (draft.localGrower)
				{
					draft.localGrower.Percent = percent;
				}
			});
		});
	}, []);

	// After a successful PUT, refresh the breakdown so it reflects the saved inputs.
	const save = useCallback(() =>
	{
		SaveDiscounts(buildSavePayload(discountState)).then(resultAction =>
		{
			if (updatePlanDiscounts.fulfilled.match(resultAction))
			{
				GetDiscountMatrix().then(() => setIsDirty(false));
			}
		});
	}, [discountState, SaveDiscounts, GetDiscountMatrix]);

	return {
		discountState,
		discountMatrix: SelectedPlan?.DiscountMatrix,
		isDirty,
		addCropLevelRow,
		updateCropLevelRow,
		deleteCropLevelRow,
		updateHybridLevelRow,
		updateLocalGrower,
		save
	};
}
